import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  pairwise,
  switchMap,
  takeUntil,
  timeout,
} from 'rxjs/operators';
import { Logger } from 'src/app/shared/tcs-logging/logger';
import { createSafeProps } from 'src/lib/tcs-app-insights/app-insights-util';
import { TickService } from 'src/app/shared/tick.service';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
import { retryBackoff } from 'src/util/rxjs/operators/retry-backoff';
import { fromFetch } from 'rxjs/fetch';
import { shareReplaySafe } from 'src/util/rxjs/operators/share-replay-safe';
import { TabsService } from '../tcs-tabs/tabs.service';

export interface NetworkInfo {
  status: OnlineStatus;
  displayStatus: string;
  quality: NetworkQuality | null;
  qualityValue: number | null;
}

export enum NetworkQuality {
  Poor = 'Poor',
  Fair = 'Fair',
  Good = 'Good',
}

export enum OnlineStatus {
  Online = 'Online',
  Offline = 'Offline',
}

type BandwidthEffectiveType = 'slow-2g' | '2g' | '3g' | '4g';
interface BandwidthStatus {
  effectiveBandwidth: number;
  effectiveType: BandwidthEffectiveType;
}

enum NetworkStatus {
  Up = 'Up',
  Down = 'Down',
}

interface NetworkConnection {
  rtt: number;
  downlink: number;
  effectiveType: BandwidthEffectiveType;
  onchange: (ev: Event) => void | null;
}

function getNetworkQuality(bandwidthStatus: BandwidthStatus): NetworkQuality | null {
  switch (bandwidthStatus?.effectiveType) {
    case 'slow-2g':
    case '2g':
      return NetworkQuality.Poor;
    case '3g':
      return NetworkQuality.Fair;
    case '4g':
      return NetworkQuality.Good;
    default:
      return null;
  }
}

const RETRY_TIMEOUT = 5_000;

@Injectable({
  providedIn: 'root',
})
export class TcsNetworkService implements OnDestroy {
  private _onDestroy$ = new Subject();
  private _networkStatus$ = new BehaviorSubject<NetworkStatus | null>(null);
  private _bandwidthStatus$ = new BehaviorSubject<BandwidthStatus | null>(null);
  private _networkInfo: NetworkConnection;
  private _downCache: {
    status: NetworkStatus;
    prevStatus: NetworkStatus | null;
    tabStatus: any;
    timestamp: Date;
  } | null;

  public networkInfo$: Observable<NetworkInfo | null>;

  private _networkStatusSupport = 'ononline' in window && 'onoffline' in window;
  private _bandwidthStatusSupport =
    this._networkStatusSupport && 'navigator' in window && 'connection' in window.navigator;

  constructor(private _logger: Logger, private _tabs: TabsService, private _tick: TickService) {
    this.networkInfo$ = combineLatest([this._networkStatus$, this._bandwidthStatus$]).pipe(
      map(([networkStatus, bandwidthStatus]) => {
        if (networkStatus == null) {
          return null;
        }
        const onlineStatus = this.toOnlineStatus(networkStatus);
        const isOnline = onlineStatus === OnlineStatus.Online;
        const quality =
          bandwidthStatus != null && isOnline ? getNetworkQuality(bandwidthStatus) : null;
        const networkData: NetworkInfo = {
          status: onlineStatus,
          displayStatus: `${onlineStatus}${quality != null ? ` (quality: ${quality})` : ''}`,
          quality,
          qualityValue:
            bandwidthStatus != null && isOnline
              ? this._toQualityValue(bandwidthStatus.effectiveType)
              : null,
        };
        return networkData;
      }),
      shareReplaySafe(1),
    );

    if (this._networkStatusSupport) {
      if (typeof navigator !== 'undefined') {
        this._changeStatus(this._boolToNetworkStatus(navigator.onLine));
        this._watchBandwidthChanges();
      }
      window.ononline = () => {
        this._changeStatus(NetworkStatus.Up);
        this._watchBandwidthChanges();
      };
      window.onoffline = () => this._changeStatus(NetworkStatus.Down);
    }

    const logStatusChange = (params: {
      status: NetworkStatus | null;
      prevStatus: NetworkStatus | null;
      tabStatus: object;
      timestamp: Date;
    }) =>
      this._logger.info(
        'NetworkService.statusChanged',
        createSafeProps({
          status: params.status,
          previous: params.prevStatus,
          statusTimestamp: params.timestamp,
          ...params.tabStatus,
        }),
      );

    this._networkStatus$
      .pipe(pairwise(), takeUntil(this._onDestroy$))
      .subscribe(([prevStatus, curStatus]) => {
        if (curStatus !== prevStatus) {
          const timestamp = new Date();
          const tabStatus = this._tabs.getStatus();
          if (curStatus === NetworkStatus.Down) {
            // cache the status, then log it when the network is back up.
            this._downCache = { status: curStatus, prevStatus, tabStatus, timestamp };

            return;
          } else if (this._downCache != null) {
            // log previous down status
            logStatusChange(this._downCache);
            this._downCache = null;
          }

          // log current status
          logStatusChange({ status: curStatus, prevStatus, tabStatus, timestamp });
        }
      });

    // since window online/offline events aren't very accurate (other than when using devtools)
    // instead poll our healthcheck endpoint to check if network is up/down.
    this._tick.everyFifteenSeconds$
      .pipe(
        switchMap(() => {
          if (typeof navigator !== 'undefined' && !navigator.onLine) {
            // navigator.onLine really only works propery when the browser is manually set to on/offline.
            // this is here so we can continue to simulate offline status through devtools.
            return of(null);
          }

          const fetch$ = fromFetch('/healthcheck', { cache: 'no-store' }).pipe(
            timeout({ each: RETRY_TIMEOUT }),
            retryBackoff({ initialDuration: 500, maxRetries: 2 }),
            catchError((err) => of({ status: 0, error: err })),
          );

          return fetch$;
        }),
        filterNull(),
        map((res) => res.status),
        distinctUntilChanged(),
        takeUntil(this._onDestroy$),
      )
      .subscribe((status) => {
        this._changeStatus(this._boolToNetworkStatus(status >= 200 && status <= 299));
      });
  }

  public toOnlineStatus(networkStatus: NetworkStatus | null) {
    return networkStatus === NetworkStatus.Up ? OnlineStatus.Online : OnlineStatus.Offline;
  }

  private _toQualityValue(effectiveType: BandwidthEffectiveType): number {
    const val = parseInt(effectiveType, 10);
    return isNaN(val) ? 1 : val;
  }

  private _watchBandwidthChanges() {
    if (this._bandwidthStatusSupport) {
      this._networkInfo = (navigator as any).connection as NetworkConnection;
      const setBandwidth = () =>
        this._bandwidthStatus$.next({
          effectiveBandwidth: this._networkInfo.downlink,
          effectiveType: this._networkInfo.effectiveType,
        });
      setBandwidth();
      let { effectiveType, downlink } = this._networkInfo;
      this._networkInfo.onchange = (ev: any) => {
        const info = ev.target as NetworkConnection;
        const minQuality = 3; //3g
        const curQuality = this._toQualityValue(info.effectiveType); //slow-2g, 2g, 3g, 4g -> 1, 2, 3, 4
        const prevQuality = this._toQualityValue(effectiveType);
        if (effectiveType !== info.effectiveType && curQuality < minQuality) {
          this._logger.warning(
            `NetworkStatus.connection bandwidth changed ${downlink}mb/s${
              downlink > 0 ? ` (${effectiveType})` : ''
            } to ${info.downlink}mb/s ${info.downlink > 0 ? `(${info.effectiveType})` : ''}`,
            createSafeProps({
              previous: { downlink, effectiveType },
              current: { downlink: info.downlink, effectiveType: info.effectiveType },
              ...this._tabs.getStatus(),
            }),
          );
        }
        setBandwidth();
        if (curQuality < minQuality || (prevQuality < minQuality && curQuality >= minQuality)) {
          effectiveType = info.effectiveType;
          downlink = info.downlink;
        }
      };
    } else {
      this._logger.warning('NetworkService bandwidth status is not supported');
    }
  }

  private _changeStatus(status: NetworkStatus) {
    this._networkStatus$.next(status);
  }

  private _boolToNetworkStatus(up: boolean) {
    return up ? NetworkStatus.Up : NetworkStatus.Down;
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
